import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ImagePermissionService } from 'app/features/capture/services/image-permission-service';
import { ButtonComponent } from 'shared/components/atoms';
import { ButtonColor, ButtonShape, ButtonSize, ButtonStyle } from 'shared/enums';
import { ImageCaptureState } from 'shared/types';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-camera',
  templateUrl: './camera.component.html',
  imports: [ButtonComponent, CommonModule, DatePipe, TranslateModule],
})
export class CameraComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() public capturedImages: EventEmitter<ImageCaptureState[]> = new EventEmitter<ImageCaptureState[]>();
  @Output() public backToReview = new EventEmitter<void>();

  @Input() public imageCount = 0;

  private mediaStream: MediaStream | null = null;
  private streamPromise: Promise<MediaStream> | null = null;
  private cameraInitialized = false;
  protected readonly ButtonShape = ButtonShape;
  protected readonly ButtonColor = ButtonColor;
  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonStyle = ButtonStyle;

  private isCaptureInProgress = false;
  public uiLogs: { message: string; isError: boolean; timestamp: Date }[] = [];
  public showLogs = false;

  private imagePermissionService = inject(ImagePermissionService);

  @ViewChild('video', { static: true }) public videoElement!: ElementRef;
  @ViewChild('canvas', { static: true }) public canvasElement!: ElementRef;

  public ngOnInit(): void {
    this.logToUI('Camera component initialized');
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      if (!this.cameraInitialized) {
        this.startCamera().catch((err) => {
          this.logToUI(`Failed to initialize camera in ngAfterViewInit: ${err}`, true);
        });
      }
    }, 100);
  }

  public async startCamera(): Promise<void> {
    if (this.cameraInitialized) {
      this.logToUI('Camera already initialized, skipping initialization');
      return;
    }

    try {
      this.logToUI('Camera started.');
      this.mediaStream = await this.getMediaStream();
      this.setupVideoElement(this.mediaStream);
    } catch (err) {
      this.logToUI(`Camera error: ${err}`, true);
      this.streamPromise = null; // Reset promise on error
      this.cameraInitialized = false;
    }
  }

  public async capture(): Promise<void> {
    if (this.isCaptureInProgress) {
      this.logToUI('Capture already in progress, please wait');
      return;
    }

    this.isCaptureInProgress = true;
    try {
      const position = await this.getGeolocation();
      const imageData = this.captureImage();
      const fileName = this.generateUniqueFileName();

      const capturedImage = new ImageCaptureState(
        imageData,
        fileName,
        true,
        position.coords.latitude,
        position.coords.longitude,
        new Date(),
      );
      this.capturedImages.emit([capturedImage]);
    } catch (error) {
      this.logToUI(`Error capturing image with location: ${error}`, true);
    } finally {
      this.isCaptureInProgress = false;
    }
  }

  public pauseCamera(): void {
    this.logToUI('Camera paused');
    this.resetVideoElement();
    this.cameraInitialized = false;
  }

  public stopCamera(): void {
    this.logToUI('Camera stopped');
    this.stopMediaStream();
    this.resetVideoElement();
    this.cameraInitialized = false;
  }

  public goToReview(): void {
    this.backToReview.emit();
  }

  private resetVideoElement(): void {
    if (this.videoElement) {
      const video = this.videoElement.nativeElement;
      video.pause();
      video.srcObject = null;
      video.src = '';
    }
  }

  private stopMediaStream(): void {
    this.logToUI('Stopping media stream');
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
      this.streamPromise = null;
    }
  }

  private setupVideoElement(stream: MediaStream): void {
    const video = this.videoElement.nativeElement;

    video.srcObject = stream;
    video
      .play()
      .then(() => {
        this.cameraInitialized = true;
        this.logToUI('Camera initialized and started.');
        this.handleAndroidAdjustments(video);
      })
      .catch((error: Error) => this.retryVideoPlayback(video, stream, error));
  }

  private retryVideoPlayback(video: HTMLVideoElement, stream: MediaStream, error: Error): void {
    this.logToUI(`Video playback error: ${error}`, true);

    setTimeout(
      () => {
        video.srcObject = stream;
        video
          .play()
          .then(() => {
            this.cameraInitialized = true;
            this.logToUI('Camera playback retry successful');
          })
          .catch((finalError) => this.logToUI(`Final video playback error: ${finalError}`, true));
      },
      this.isAndroid() ? 500 : 200,
    );
  }

  private handleAndroidAdjustments(video: HTMLVideoElement): void {
    if (!this.isAndroid()) return;

    const isPortrait = window.innerHeight > window.innerWidth;
    video.width = isPortrait ? 720 : 1280;
    video.height = isPortrait ? 1280 : 720;

    video.style.transform = 'translateZ(0)';
    this.logToUI('Android-specific adjustments applied');
  }

  private captureImage(): string {
    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context?.drawImage(video, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL('image/jpeg', 0.9);
  }

  private async getMediaStream(): Promise<MediaStream> {
    if (!this.streamPromise) {
      const isPortrait = window.innerHeight > window.innerWidth;

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === 'videoinput');
      const rearCamera = videoDevices.find(
        (device) => device.label.toLowerCase().includes('back') || device.label.toLowerCase().includes('rear'),
      );

      const constraints: MediaStreamConstraints = {
        video: {
          width: isPortrait ? 720 : 1280,
          height: isPortrait ? 1280 : 720,
          facingMode: 'environment',
        },
        audio: false,
      };

      if (rearCamera) {
        this.logToUI('Rear view camera found and set device id.');
        constraints.video = {
          width: isPortrait ? 720 : 1280,
          height: isPortrait ? 1280 : 720,
          deviceId: { exact: rearCamera.deviceId },
        };
      }

      this.streamPromise = navigator.mediaDevices.getUserMedia(constraints);
    }
    return this.streamPromise;
  }

  private async getGeolocation(): Promise<GeolocationPosition> {
    return new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.logToUI('Geolocation retrieved successfully');
          resolve(position);
        },
        (error) => {
          this.handleGeolocationError(error);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  }

  private handleGeolocationError(error: GeolocationPositionError): void {
    let errorMessage = 'An unknown error occurred while retrieving geolocation.';
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'User denied the request for Geolocation.';
        this.imagePermissionService.showPermissionDeniedDialog(true); // Notify the user
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Location information is unavailable.';
        break;
      case error.TIMEOUT:
        errorMessage = 'The request to get user location timed out.';
        break;
      default:
        errorMessage = 'An unknown error occurred.';
        break;
    }
    // Log the error
    this.logToUI(`Geolocation error: ${errorMessage}`, true);
    console.error('Geolocation error details:', error);
  }

  private isAndroid(): boolean {
    return /Android/i.test(navigator.userAgent);
  }

  private generateUniqueFileName(): string {
    const timestamp = new Date().getTime();
    const uuid = uuidv4();
    return `image_${timestamp}_${uuid}.jpg`;
  }

  private logToUI(message: string, isError = false): void {
    // Log to console
    if (isError) {
      console.error(message);
    } else {
      console.log(message);
    }

    // Add to UI logs
    this.uiLogs.unshift({
      message,
      isError,
      timestamp: new Date(),
    });

    // Limit the number of logs to prevent memory issues
    if (this.uiLogs.length > 50) {
      this.uiLogs.pop();
    }
  }

  public toggleLogs(): void {
    this.showLogs = !this.showLogs;
  }

  public clearLogs(): void {
    this.uiLogs = [];
  }

  public ngOnDestroy(): void {
    this.stopCamera();
  }
}
